import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMatch } from '../game/MatchContext';
import { AI_STRATEGIES, aiStrategyInfo, LEVELS, PAPER_URL } from '../game/model.mjs';
import { getConfig } from '../services/quantumBattleshipApi';
import { Button, Modal, Notice, PageHeading } from '../components/UI';
import { OpeningBoard } from '../components/OpeningBoard';

export default function Setup() {
  const { settings, begin, game, sessionId, loading, error, busy } = useMatch();
  const [level, setLevel] = useState(settings.level);
  const [aiStrategy, setAiStrategy] = useState(settings.aiStrategy);
  const [executionMode, setExecutionMode] = useState(settings.executionMode);
  const [config, setConfig] = useState(null);
  const [configError, setConfigError] = useState(false);
  const [replace, setReplace] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    getConfig().then(value => { if (!cancelled) setConfig(value); }).catch(() => { if (!cancelled) setConfigError(true); });
    return () => { cancelled = true; };
  }, []);
  function start() { begin({ level, executionMode, aiStrategy }); navigate('/ship-placement'); }
  function resume() { navigate(game?.status === 'completed' ? '/game-results' : game?.status === 'setup' ? '/ship-placement' : '/battle-interface'); }
  return <div className="setup-page">
    <div className="setup-layout">
    <OpeningBoard level={level} />
    <section className="setup-choices" aria-label="New game options">
    <PageHeading title="New game" subtitle="Choose a rule level, then place your fleet." />
    <fieldset className="level-list"><legend className="sr-only">Rule level</legend>{LEVELS.map(item => <label key={item.id} className={`level-option ${level === item.id ? 'chosen' : ''}`}>
      <input type="radio" name="level" value={item.id} checked={level === item.id} onChange={() => setLevel(item.id)} />
      <span><strong>{item.id} · {item.name}</strong><span className="level-description">{item.description}</span></span>
    </label>)}</fieldset>
    <div className="ai-choice">
      <label className="form-label" htmlFor="ai-strategy">Computer targeting</label>
      <select id="ai-strategy" value={aiStrategy} onChange={event => setAiStrategy(event.target.value)} aria-describedby="ai-strategy-description">
        {AI_STRATEGIES.map(item => <option key={item.id} value={item.id}>{item.name}</option>)}
      </select>
      <p id="ai-strategy-description" className="small muted">{aiStrategyInfo(aiStrategy)?.description}</p>
    </div>
    <p className="setup-facts">Three ships (2, 3 and 4 squares). You move first.</p>
    <div className="setup-actions"><Button primary disabled={busy} onClick={() => game?.status === 'active' ? setReplace(true) : start()}>Place fleet</Button>
      {sessionId && <Button onClick={resume} disabled={loading || busy}>{loading ? 'Checking saved match…' : game?.status === 'completed' ? 'View last match' : 'Resume match'}</Button>}
    </div>
    {error && !game && <Notice error>{error}</Notice>}
    <details className="execution-options"><summary>Execution options</summary><div className="details-body">
      <label className="form-label" htmlFor="execution">Rule-transition sampling</label>
      <select id="execution" value={executionMode} onChange={event => setExecutionMode(event.target.value)}>
        <option value="simulation">Simulation</option><option value="hardware" disabled={!config?.hardware_available}>IBM hardware{config?.hardware_available ? '' : ' (unavailable)'}</option>
      </select>
      <p className="muted small">{configError ? 'Server configuration could not be checked. Simulation will be requested when you start.' : !config ? 'Checking server configuration…' : config.hardware_available ? 'Hardware may take longer to return a measurement.' : 'Hardware execution is not enabled on this server.'}</p>
    </div></details>
    </section>
    </div>
    <div className="research-note"><h2>About this project</h2><p>This is a playable interface for a classical stochastic Battleship model with circuit-sampled rule transitions. It does not claim quantum advantage or physical entanglement of ships.</p>
      <p><a href={PAPER_URL} target="_blank" rel="noreferrer">Read the research paper <span className="sr-only">(opens in a new tab)</span></a></p>
    </div>
    {replace && <Modal title="Start a new match?" onClose={() => setReplace(false)} footer={<><Button onClick={() => setReplace(false)}>Keep current match</Button><Button primary onClick={start}>New match</Button></>}><p>This will replace the saved match on this browser.</p></Modal>}
  </div>;
}
